'use client';

import { useCallback, useEffect, useState } from 'react';
import Sidebar from './Sidebar';
import Topbar from './Topbar';
import Dashboard from '../views/Dashboard';
import Usuarios from '../views/Usuarios';
import Placeholder from '../views/Placeholder';
import LibroRadicador from '../views/LibroRadicador';
import ExpedientesNoIniciados from '../views/ExpedientesNoIniciados';
import MisExpedientes from '../views/MisExpedientes';
import BandejaDecisiones from '../views/BandejaDecisiones';
import BandejaReparto from '../views/BandejaReparto';
import BandejaJuzgamiento from '../views/BandejaJuzgamiento';
import BandejaSegundaInstancia from '../views/BandejaSegundaInstancia';
import CargaLaboral from '../views/CargaLaboral';
import CentralNotificaciones from '../views/CentralNotificaciones';
import Reportes from '../views/Reportes';
import ExpedienteDetalle from '../views/ExpedienteDetalle';
import MisActuaciones from '../views/MisActuaciones';
import MiFirma from '../views/MiFirma';
import { ROLES_PARTE_UI } from '../lib/menu';
import type { Sesion } from './Login';

// Contenedor de la app autenticada: menú + barra + contenido según la vista activa.
// Las vistas aún no construidas caen en el Placeholder "en construcción".
export default function AppShell({
  usuario,
  onLogout,
}: {
  usuario: Sesion;
  onLogout: () => void;
}) {
  // Las partes del portal (disciplinado/defensor) arrancan en "Mis actuaciones",
  // su única vista; el resto en el tablero de inicio.
  const [vista, setVista] = useState(
    ROLES_PARTE_UI.includes(usuario.rol) ? 'mis-actuaciones' : 'inicio',
  );
  // Id del expediente seleccionado para abrir el detalle (vista 'expediente').
  const [expedienteId, setExpedienteId] = useState<string | null>(null);

  // Navega al detalle de un expediente: guarda el id y cambia de vista.
  const abrirExpediente = useCallback((id: string) => {
    setExpedienteId(id);
    if (typeof window !== 'undefined') localStorage.setItem('expedienteSeleccionado', id);
    setVista('expediente');
  }, []);

  // Abre el Libro radicador en modo edición de la Etapa 2 (caso devuelto por el especialista).
  const editarEtapa2 = useCallback((id: string) => {
    if (typeof window !== 'undefined') localStorage.setItem('expedienteEditar', id);
    setVista('radicar-queja');
  }, []);

  function contenido() {
    switch (vista) {
      case 'inicio':
        return <Dashboard usuario={usuario} />;
      case 'usuarios':
        return <Usuarios />;
      case 'expedientes-no-iniciados':
        return <ExpedientesNoIniciados onIrARadicar={() => setVista('radicar-queja')} />;
      case 'radicar-queja':
        return <LibroRadicador onVolverExpediente={abrirExpediente} />;
      case 'mis-expedientes':
        return <MisExpedientes onAbrir={abrirExpediente} onEditarEtapa2={editarEtapa2} />;
      case 'decision-inicial':
        return <BandejaDecisiones onAbrir={abrirExpediente} />;
      case 'reparto-bandeja':
        return <BandejaReparto onAbrir={abrirExpediente} />;
      case 'juzgamiento-bandeja':
        return <BandejaJuzgamiento onAbrir={abrirExpediente} />;
      case 'segunda-bandeja':
        return <BandejaSegundaInstancia onAbrir={abrirExpediente} />;
      case 'reparto-carga':
        return <CargaLaboral />;
      case 'notificaciones-central':
        return <CentralNotificaciones />;
      case 'reportes':
        return <Reportes />;
      case 'mis-actuaciones':
        return <MisActuaciones />;
      case 'mi-firma':
        return <MiFirma />;
      case 'expediente':
        return expedienteId ? (
          <ExpedienteDetalle expedienteId={expedienteId} onVolver={() => setVista('mis-expedientes')} onEditarEtapa2={editarEtapa2} />
        ) : (
          <Placeholder vista={vista} />
        );
      default:
        return <Placeholder vista={vista} />;
    }
  }

  // La app autenticada maneja su propio scroll en <main>; el <body> no debe crecer.
  // Bloqueamos su overflow mientras AppShell está montado (login/portal, que sí usan
  // scroll de página con min-h-screen, quedan intactos) para que los portales de Radix
  // (desplegables/diálogos, montados en document.body) no extiendan la página y dejen
  // ese espacio en blanco al final al hacer scroll.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <Sidebar usuario={usuario} vista={vista} onNavegar={setVista} />
      <div className="flex flex-1 flex-col overflow-hidden">
        <Topbar usuario={usuario} onLogout={onLogout} onAbrirExpediente={abrirExpediente} />
        <main className="flex-1 overflow-y-auto p-6">{contenido()}</main>
      </div>
    </div>
  );
}
